import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiX, FiUser, FiMail, FiPhone, FiBriefcase, FiUpload, FiFileText, FiLinkedin, FiCalendar, FiSend, FiCheck, FiAlertCircle, FiFile } from 'react-icons/fi';
import './CareerModal.css';

const CareerModal = ({ isOpen, onClose, positionTitle = '', positionId = '' }) => {
  // Common job titles for autocomplete
  const jobTitles = [
    // IT/Software Development (30 positions)
    'Software Engineer',
    'Senior Software Engineer',
    'Lead Software Engineer',
    'Full Stack Developer',
    'Senior Full Stack Developer',
    'Frontend Developer',
    'Senior Frontend Developer',
    'Backend Developer',
    'Senior Backend Developer',
    'DevOps Engineer',
    'Senior DevOps Engineer',
    'Cloud Engineer',
    'Senior Cloud Engineer',
    'Data Engineer',
    'Senior Data Engineer',
    'Machine Learning Engineer',
    'AI/ML Engineer',
    'Data Scientist',
    'Senior Data Scientist',
    'QA Engineer',
    'Test Automation Engineer',
    'Systems Administrator',
    'Database Administrator',
    'IT Support Specialist',
    'Network Engineer',
    'Solutions Architect',
    'Technical Lead',
    'Engineering Manager',
    // Accounting/Bookkeeping (5 positions)
    'Accountant',
    'Senior Accountant',
    'Bookkeeper',
    'Financial Analyst',
    'Tax Accountant'
  ];

  const workAuthorizationOptions = [
    'CPT EAD (Curricular Practical Training)',
    'OPT EAD (Optional Practical Training)',
    'H-1B Visa',
    'H-4 EAD',
    'L-1 Visa',
    'TN Visa (NAFTA)',
    'EAD (Green Card in Process)',
    'Green Card Holder',
    'Asylum/Refugee EAD',
    'Other'
  ];

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    position: positionTitle === 'General Application' ? '' : positionTitle,
    coverLetter: '',
    linkedinProfile: '',
    availableStartDate: '',
    isUSCitizen: '',
    workAuthorization: ''
  });

  const [resumeFile, setResumeFile] = useState(null);
  const fileInputRef = useRef(null);
  const positionInputRef = useRef(null);

  const [formState, setFormState] = useState({
    isSubmitting: false,
    isSubmitted: false,
    error: null
  });

  const [validationErrors, setValidationErrors] = useState({});
  const [showPositionDropdown, setShowPositionDropdown] = useState(false);
  const [filteredPositions, setFilteredPositions] = useState([]);

  // Update position field when positionTitle prop changes
  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      position: positionTitle === 'General Application' ? '' : positionTitle
    }));
  }, [positionTitle]);

  const validateForm = () => {
    const errors = {};

    if (!formData.name.trim() || formData.name.trim().length < 2) {
      errors.name = 'Name must be at least 2 characters long';
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email.trim() || !emailRegex.test(formData.email)) {
      errors.email = 'Please enter a valid email address';
    }

    const phoneRegex = /^[\+]?[1-9][\d]{0,15}$/;
    if (!formData.phone.trim() || !phoneRegex.test(formData.phone.replace(/[\s\-\(\)]/g, ''))) {
      errors.phone = 'Please enter a valid phone number';
    }

    if (!formData.position.trim()) {
      errors.position = 'Please specify the position you are applying for';
    }

    if (formData.coverLetter.trim() && formData.coverLetter.trim().length < 50) {
      errors.coverLetter = 'Cover letter must be at least 50 characters long if provided';
    }

    if (!resumeFile) {
      errors.resume = 'Please upload your resume';
    } else {
      // Validate file type and size
      const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
      const maxSize = 5 * 1024 * 1024; // 5MB

      if (!allowedTypes.includes(resumeFile.type)) {
        errors.resume = 'Resume must be a PDF, DOC, or DOCX file';
      } else if (resumeFile.size > maxSize) {
        errors.resume = 'Resume file size must be less than 5MB';
      }
    }

    // Validate LinkedIn URL if provided
    if (formData.linkedinProfile.trim()) {
      const linkedinRegex = /^https?:\/\/(www\.)?linkedin\.com\/in\/[a-zA-Z0-9-]+\/?$/;
      if (!linkedinRegex.test(formData.linkedinProfile.trim())) {
        errors.linkedinProfile = 'Please enter a valid LinkedIn profile URL';
      }
    }

    // Validate citizenship status
    if (!formData.isUSCitizen) {
      errors.isUSCitizen = 'Please select your citizenship status';
    }

    // Validate work authorization for non-citizens
    if (formData.isUSCitizen === 'no' && !formData.workAuthorization) {
      errors.workAuthorization = 'Please select your work authorization status';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // If changing citizenship, clear work authorization appropriately
    if (name === 'isUSCitizen') {
      setFormData(prev => ({
        ...prev,
        isUSCitizen: value,
        workAuthorization: '' // Clear work authorization when changing citizenship status
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }

    // Handle position autocomplete
    if (name === 'position') {
      if (value.trim().length > 0) {
        const filtered = jobTitles.filter(title =>
          title.toLowerCase().includes(value.toLowerCase())
        );
        setFilteredPositions(filtered);
        setShowPositionDropdown(filtered.length > 0);
      } else {
        setFilteredPositions([]);
        setShowPositionDropdown(false);
      }
    }

    // Clear validation error when user starts typing
    if (validationErrors[name]) {
      setValidationErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handlePositionSelect = (position) => {
    setFormData(prev => ({
      ...prev,
      position: position
    }));
    setShowPositionDropdown(false);
    setFilteredPositions([]);
  };

  const handlePositionFocus = () => {
    if (formData.position.trim().length > 0) {
      const filtered = jobTitles.filter(title =>
        title.toLowerCase().includes(formData.position.toLowerCase())
      );
      setFilteredPositions(filtered);
      setShowPositionDropdown(filtered.length > 0);
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (positionInputRef.current && !positionInputRef.current.contains(event.target)) {
        setShowPositionDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setResumeFile(file);

    // Clear resume validation error
    if (validationErrors.resume) {
      setValidationErrors(prev => ({
        ...prev,
        resume: ''
      }));
    }
  };

  const handleFileButtonClick = () => {
    fileInputRef.current?.click();
  };

  const removeFile = () => {
    setResumeFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  const convertFileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        // Remove the data:mime/type;base64, prefix
        const base64String = reader.result.split(',')[1];
        resolve(base64String);
      };
      reader.onerror = (error) => reject(error);
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setFormState({
      isSubmitting: true,
      isSubmitted: false,
      error: null
    });

    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';

      // Prepare career application data for the proper career endpoint
      const applicationData = {
        name: formData.name.trim(),
        email: formData.email.trim(),
        phone: formData.phone.trim(),
        position: formData.position.trim(),
        position_id: positionId || null,
        cover_letter: formData.coverLetter.trim(),
        linkedin_profile: formData.linkedinProfile.trim() || null,
        available_start_date: formData.availableStartDate.trim() || null,
        is_us_citizen: formData.isUSCitizen,
        work_authorization: formData.isUSCitizen === 'no' ? formData.workAuthorization : null,
        resume_filename: resumeFile ? resumeFile.name : null,
        resume_size: resumeFile ? resumeFile.size : null
      };

      // Add resume attachment if file is selected
      if (resumeFile) {
        const base64Content = await convertFileToBase64(resumeFile);
        applicationData.attachments = [
          {
            filename: resumeFile.name,
            fileblob: base64Content,
            mimetype: resumeFile.type || 'application/octet-stream'
          }
        ];
      }

      const response = await fetch(`${apiUrl}/api/careers`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(applicationData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to submit application');
      }

      const result = await response.json();

      setFormState({
        isSubmitting: false,
        isSubmitted: true,
        error: null
      });

      // Auto-close after 4 seconds (a bit longer for career applications)
      setTimeout(() => {
        handleClose();
      }, 4000);

    } catch (error) {
      console.error('Error submitting career application:', error);
      setFormState({
        isSubmitting: false,
        isSubmitted: false,
        error: error.message || 'Failed to submit application. Please try again.'
      });
    }
  };

  const handleClose = () => {
    if (!formState.isSubmitting) {
      setFormData({
        name: '',
        email: '',
        phone: '',
        position: positionTitle,
        coverLetter: '',
        linkedinProfile: '',
        availableStartDate: '',
        isUSCitizen: '',
        workAuthorization: ''
      });
      setResumeFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      setFormState({
        isSubmitting: false,
        isSubmitted: false,
        error: null
      });
      setValidationErrors({});
      onClose();
    }
  };

  const modalVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.3,
        ease: "easeOut"
      }
    },
    exit: {
      opacity: 0,
      scale: 0.8,
      transition: {
        duration: 0.2,
        ease: "easeIn"
      }
    }
  };

  const overlayVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1 },
    exit: { opacity: 0 }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        className="career-modal-overlay"
        variants={overlayVariants}
        initial="hidden"
        animate="visible"
        exit="exit"
        onClick={handleClose}
      >
        <motion.div
          className="career-modal"
          variants={modalVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="career-modal-header">
            <div className="career-modal-title">
              <h2>
                {formState.isSubmitted ? (
                  <>
                    <FiCheck className="success-icon" />
                    Application Submitted!
                  </>
                ) : (
                  <>
                    <FiBriefcase />
                    Apply for Position
                  </>
                )}
              </h2>
              {!formState.isSubmitted && (
                <p className="career-modal-subtitle">
                  {positionTitle
                    ? `Submit your application for the ${positionTitle} position.`
                    : "Submit your application and join our growing team."
                  }
                </p>
              )}
            </div>
            <button
              className="career-modal-close"
              onClick={handleClose}
              disabled={formState.isSubmitting}
            >
              <FiX />
            </button>
          </div>

          {/* Content */}
          <div className="career-modal-content">
            {formState.isSubmitted ? (
              <motion.div
                className="success-message"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
              >
                <div className="success-icon-large">
                  <FiCheck />
                </div>
                <h3>Thank you for your application!</h3>
                <p>We've received your application for the {formData.position} position.</p>
                <p>Our HR team will review your application and get back to you within 5-7 business days.</p>
                <p className="auto-close-text">This window will close automatically...</p>
              </motion.div>
            ) : (
              <form onSubmit={handleSubmit} className="career-modal-form">
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">
                      <FiUser className="form-icon" />
                      Full Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className={`form-input ${validationErrors.name ? 'error' : ''}`}
                      placeholder="Your full name"
                      disabled={formState.isSubmitting}
                      required
                    />
                    {validationErrors.name && (
                      <span className="error-message">
                        <FiAlertCircle />
                        {validationErrors.name}
                      </span>
                    )}
                  </div>

                  <div className="form-group">
                    <label className="form-label">
                      <FiMail className="form-icon" />
                      Email Address *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className={`form-input ${validationErrors.email ? 'error' : ''}`}
                      placeholder="your.email@example.com"
                      disabled={formState.isSubmitting}
                      required
                    />
                    {validationErrors.email && (
                      <span className="error-message">
                        <FiAlertCircle />
                        {validationErrors.email}
                      </span>
                    )}
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">
                      <FiPhone className="form-icon" />
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className={`form-input ${validationErrors.phone ? 'error' : ''}`}
                      placeholder="+1 (555) 123-4567"
                      disabled={formState.isSubmitting}
                      required
                    />
                    {validationErrors.phone && (
                      <span className="error-message">
                        <FiAlertCircle />
                        {validationErrors.phone}
                      </span>
                    )}
                  </div>

                  <div className="form-group" ref={positionInputRef} style={{ position: 'relative' }}>
                    <label className="form-label">
                      <FiBriefcase className="form-icon" />
                      Position *
                    </label>
                    <input
                      type="text"
                      name="position"
                      value={formData.position}
                      onChange={handleInputChange}
                      onFocus={handlePositionFocus}
                      className={`form-input ${validationErrors.position ? 'error' : ''}`}
                      placeholder="Enter your job title"
                      disabled={formState.isSubmitting}
                      required
                      autoComplete="off"
                    />
                    {showPositionDropdown && filteredPositions.length > 0 && (
                      <div className="position-dropdown">
                        {filteredPositions.map((position, index) => (
                          <div
                            key={index}
                            className="position-dropdown-item"
                            onClick={() => handlePositionSelect(position)}
                          >
                            {position}
                          </div>
                        ))}
                      </div>
                    )}
                    {validationErrors.position && (
                      <span className="error-message">
                        <FiAlertCircle />
                        {validationErrors.position}
                      </span>
                    )}
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">
                      <FiLinkedin className="form-icon" />
                      LinkedIn Profile
                    </label>
                    <input
                      type="url"
                      name="linkedinProfile"
                      value={formData.linkedinProfile}
                      onChange={handleInputChange}
                      className={`form-input ${validationErrors.linkedinProfile ? 'error' : ''}`}
                      placeholder="https://linkedin.com/in/yourprofile"
                      disabled={formState.isSubmitting}
                    />
                    {validationErrors.linkedinProfile && (
                      <span className="error-message">
                        <FiAlertCircle />
                        {validationErrors.linkedinProfile}
                      </span>
                    )}
                  </div>

                  <div className="form-group">
                    <label className="form-label">
                      <FiCalendar className="form-icon" />
                      Available Start Date
                    </label>
                    <input
                      type="date"
                      name="availableStartDate"
                      value={formData.availableStartDate}
                      onChange={handleInputChange}
                      className="form-input"
                      disabled={formState.isSubmitting}
                      min={new Date().toISOString().split('T')[0]}
                    />
                  </div>
                </div>

                {/* US Citizenship Status */}
                <div className="form-group">
                  <label className="form-label">
                    <FiBriefcase className="form-icon" />
                    Are you a US Citizen? *
                  </label>
                  <div className="radio-group">
                    <label className="radio-label">
                      <input
                        type="radio"
                        name="isUSCitizen"
                        value="yes"
                        checked={formData.isUSCitizen === 'yes'}
                        onChange={handleInputChange}
                        disabled={formState.isSubmitting}
                      />
                      <span>Yes</span>
                    </label>
                    <label className="radio-label">
                      <input
                        type="radio"
                        name="isUSCitizen"
                        value="no"
                        checked={formData.isUSCitizen === 'no'}
                        onChange={handleInputChange}
                        disabled={formState.isSubmitting}
                      />
                      <span>No</span>
                    </label>
                  </div>
                  {validationErrors.isUSCitizen && (
                    <span className="error-message">
                      <FiAlertCircle />
                      {validationErrors.isUSCitizen}
                    </span>
                  )}

                  {/* Background Verification Notice - Show when Yes is selected */}
                  {formData.isUSCitizen === 'yes' && (
                    <div className="citizenship-info-box">
                      <p className="citizenship-info-title">📋 Background Verification Requirements</p>
                      <p className="citizenship-info-text">
                        If selected for employment, you will be required to provide documentation
                        for background verification purposes. Acceptable documents include US Passport, Driver's License or State ID, Birth Certificate, and Social Security Card.
                      </p>
                      <p className="citizenship-info-note">
                        You will need to provide at least two of these documents during the onboarding process.
                      </p>
                    </div>
                  )}
                </div>

                {/* Work Authorization - Only show if not US Citizen */}
                {formData.isUSCitizen === 'no' && (
                  <div className="form-group">
                    <label className="form-label">
                      <FiBriefcase className="form-icon" />
                      Work Authorization Status *
                    </label>
                    <select
                      name="workAuthorization"
                      value={formData.workAuthorization}
                      onChange={handleInputChange}
                      className={`form-input ${validationErrors.workAuthorization ? 'error' : ''}`}
                      disabled={formState.isSubmitting}
                    >
                      <option value="">Select work authorization</option>
                      {workAuthorizationOptions.map((option, index) => (
                        <option key={index} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                    {validationErrors.workAuthorization && (
                      <span className="error-message">
                        <FiAlertCircle />
                        {validationErrors.workAuthorization}
                      </span>
                    )}

                    {/* Work Authorization Document Notice - Show when option is selected */}
                    {formData.workAuthorization && (
                      <div className="citizenship-info-box">
                        <p className="citizenship-info-title">📋 Work Authorization Verification</p>
                        <p className="citizenship-info-text">
                          If selected for employment, you will be required to provide documentation
                          to verify your work authorization status. Acceptable documents include valid EAD card, visa documentation, I-94 form, and passport with appropriate stamps or endorsements.
                        </p>
                        <p className="citizenship-info-note">
                          You will need to provide at least two of these documents during the onboarding process.
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {/* Resume Upload */}
                <div className="form-group">
                  <label className="form-label">
                    <FiUpload className="form-icon" />
                    Resume *
                  </label>
                  <div className="file-upload-container">
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleFileChange}
                      accept=".pdf,.doc,.docx"
                      style={{ display: 'none' }}
                      disabled={formState.isSubmitting}
                    />
                    {!resumeFile ? (
                      <button
                        type="button"
                        onClick={handleFileButtonClick}
                        className={`file-upload-button ${validationErrors.resume ? 'error' : ''}`}
                        disabled={formState.isSubmitting}
                      >
                        <FiUpload />
                        Choose Resume File
                        <span className="file-hint">PDF, DOC, or DOCX (max 5MB)</span>
                      </button>
                    ) : (
                      <div className="file-selected">
                        <div className="file-info">
                          <FiFile className="file-icon" />
                          <div className="file-details">
                            <span className="file-name">{resumeFile.name}</span>
                            <span className="file-size">{formatFileSize(resumeFile.size)}</span>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={removeFile}
                          className="file-remove"
                          disabled={formState.isSubmitting}
                        >
                          <FiX />
                        </button>
                      </div>
                    )}
                  </div>
                  {validationErrors.resume && (
                    <span className="error-message">
                      <FiAlertCircle />
                      {validationErrors.resume}
                    </span>
                  )}
                </div>

                <div className="form-group">
                  <label className="form-label">
                    <FiFileText className="form-icon" />
                    Cover Letter (Highly Recommended)
                  </label>
                  <textarea
                    name="coverLetter"
                    value={formData.coverLetter}
                    onChange={handleInputChange}
                    className={`form-textarea ${validationErrors.coverLetter ? 'error' : ''}`}
                    placeholder="Optional, but highly recommended! Tell us about your experience, why you're interested in this position, and what you can bring to our team. A thoughtful cover letter significantly increases your chances of being selected."
                    rows="5"
                    disabled={formState.isSubmitting}
                  />
                  {validationErrors.coverLetter && (
                    <span className="error-message">
                      <FiAlertCircle />
                      {validationErrors.coverLetter}
                    </span>
                  )}
                </div>

                {formState.error && (
                  <div className="form-error">
                    <FiAlertCircle />
                    {formState.error}
                  </div>
                )}

                <div className="form-actions">
                  <button
                    type="button"
                    onClick={handleClose}
                    className="btn-secondary"
                    disabled={formState.isSubmitting}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn-primary"
                    disabled={formState.isSubmitting}
                  >
                    {formState.isSubmitting ? (
                      <>
                        <div className="loading-spinner" />
                        Submitting...
                      </>
                    ) : (
                      <>
                        <FiSend />
                        Submit Application
                      </>
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default CareerModal;
